// The inner handshake of a tunnel path (DECISION-0196, GUARDRAIL relays-carry-only-ciphertext). A tunnel ends its TLS
// at the provider, so nothing is sent on the socket, `hello` and `auth` included, until the server has proved with the
// pinned DTLS certificate that it is the server. Wire format, transcript and key schedule: packages/server/src/secure.rs;
// the shared vectors are packages/server/testdata/secure-vectors.json. WebCrypto only (P-256 ECDH and ECDSA, HKDF, AES-GCM).
//
// The host rule (owner decision 2026-11-03): the host in the transcript is the one this client names in its first
// message, `new URL(target).host.toLowerCase()` (a non-default port included, as the URL parser keeps it), and the
// server signs that string as received. It is never the server's `Host` header, which a proxy may rewrite, give a
// port or re-case. The client verifies the signature against the same string it sent, so a signature made for another
// host does not verify.
import { wsUrl } from './servers';
import type { Transport } from './transport';
import { LANE_TERM, LANE_UPLOAD } from './ws';

/** The WebSocket subprotocol that asks the server for this handshake. */
export const SECURE_SUBPROTOCOL = 'termenbox-secure.v1';
const MAGIC = new Uint8Array([0x54, 0x42, 0x53, 0x31]); // "TBS1"
const NONCE_LEN = 32;
const POINT_LEN = 65;
const SIG_LEN = 64;
const TAG_LEN = 16;
/** The longest plaintext a record may carry; a peer cannot make the client buffer more. */
export const MAX_PLAIN = 1 << 20;
const LANE_TEXT = 0;
const enc = new TextEncoder();
const dec = new TextDecoder('utf-8', { fatal: true });

// The fixed prefix of an X.509 SubjectPublicKeyInfo for a P-256 key; the 65-byte point follows it.
const SPKI_PREFIX = new Uint8Array([
  0x30, 0x59, 0x30, 0x13, 0x06, 0x07, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x02, 0x01, 0x06, 0x08, 0x2a, 0x86, 0x48, 0xce, 0x3d, 0x03, 0x01, 0x07, 0x03, 0x42, 0x00,
]);

/** Not a view over a SharedArrayBuffer, which is what WebCrypto's types ask for. */
type Bytes = Uint8Array<ArrayBuffer>;

const subtle = () => globalThis.crypto.subtle;
const concat = (...parts: Uint8Array[]): Bytes => {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
};
const equal = (a: Bytes, b: Bytes) => a.length === b.length && a.every((x, i) => x === b[i]);

/** The 32 bytes of `sha-256 AB:CD:...` (the form every pinned fingerprint is kept in). */
export function fingerprintBytes(fp: string): Bytes {
  const hex = fp.trim().replace(/^sha-256\s+/i, '').split(':');
  if (hex.length !== 32 || hex.some((h) => !/^[0-9a-fA-F]{2}$/.test(h))) throw new Error('bad fingerprint');
  return Uint8Array.from(hex.map((h) => parseInt(h, 16)));
}

/** The P-256 public key of a certificate, as an SPKI that WebCrypto imports. */
export function spkiOf(der: Bytes): Bytes {
  for (let i = 0; i + SPKI_PREFIX.length + POINT_LEN <= der.length; i++) {
    if (SPKI_PREFIX.every((b, j) => der[i + j] === b) && der[i + SPKI_PREFIX.length] === 0x04) {
      return der.slice(i, i + SPKI_PREFIX.length + POINT_LEN);
    }
  }
  throw new Error('no P-256 key in the certificate');
}

function transcript(host: string, nonceC: Bytes, nonceS: Bytes, ephS: Bytes, certHash: Bytes): Bytes {
  const h = enc.encode(host);
  return concat(enc.encode('termenbox-secure-v1 server\0'), new Uint8Array([h.length >> 8, h.length & 0xff]), h, nonceC, nonceS, ephS, certHash);
}

async function hkdf(z: Bytes, salt: Bytes, info: string): Promise<Bytes> {
  const base = await subtle().importKey('raw', z, 'HKDF', false, ['deriveBits']);
  return new Uint8Array(await subtle().deriveBits({ name: 'HKDF', hash: 'SHA-256', salt, info: enc.encode(info) }, base, 256));
}

/** One direction of the record layer: AES-256-GCM with a counter nonce nobody sends. */
export class Cipher {
  private counter = 0;
  constructor(private key: CryptoKey) {}

  private nonce(n: number): Bytes {
    const iv = new Uint8Array(12);
    const v = new DataView(iv.buffer);
    v.setUint32(4, Math.floor(n / 2 ** 32));
    v.setUint32(8, n >>> 0);
    return iv;
  }

  /** The counter is taken now, so records are numbered in the order they were asked for. */
  seal(plain: Bytes): Promise<Bytes> {
    if (plain.length > MAX_PLAIN) return Promise.reject(new Error('record too long'));
    const iv = this.nonce(this.counter++);
    return subtle().encrypt({ name: 'AES-GCM', iv }, this.key, plain).then((b) => new Uint8Array(b));
  }

  open(record: Bytes): Promise<Bytes> {
    if (record.length < TAG_LEN || record.length > MAX_PLAIN + TAG_LEN) return Promise.reject(new Error('bad record length'));
    const iv = this.nonce(this.counter++);
    return subtle().decrypt({ name: 'AES-GCM', iv }, this.key, record).then((b) => new Uint8Array(b));
  }
}

const aesKey = (raw: Bytes) => subtle().importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);

/** The client's side of the exchange. Nothing it produces is anything but a handshake message. */
export class Handshake {
  private nonceC = globalThis.crypto.getRandomValues(new Uint8Array(NONCE_LEN));

  /** `pin` is the SHA-256 of the certificate DER the client was given; `host` is the one it connected to. */
  constructor(private host: string, private pin: Bytes, private ephemeral?: CryptoKeyPair) {}

  /** The first message: the magic, the nonce, and the host this client connected to (u16 length, UTF-8). */
  hello(): Bytes {
    const h = enc.encode(this.host);
    return concat(MAGIC, this.nonceC, new Uint8Array([h.length >> 8, h.length & 0xff]), h);
  }

  /** Checks the server's answer, in this order: the pin, then the signature. Throws on any failure. */
  async answer(s1: Bytes): Promise<{ message: Bytes; send: Cipher; recv: Cipher }> {
    if (s1.length < 6 || !equal(s1.subarray(0, 4), MAGIC)) throw new Error('bad server hello');
    const n = (s1[4] << 8) | s1[5];
    if (s1.length !== 6 + n + POINT_LEN + NONCE_LEN + SIG_LEN) throw new Error('bad server hello');
    const der = s1.subarray(6, 6 + n);
    const ephS = s1.subarray(6 + n, 6 + n + POINT_LEN);
    const nonceS = s1.subarray(6 + n + POINT_LEN, 6 + n + POINT_LEN + NONCE_LEN);
    const sig = s1.subarray(6 + n + POINT_LEN + NONCE_LEN);
    const certHash = new Uint8Array(await subtle().digest('SHA-256', der));
    if (!equal(certHash, this.pin)) throw new Error('the server certificate is not the pinned one');
    const signer = await subtle().importKey('spki', spkiOf(der), { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
    const signed = transcript(this.host, this.nonceC, nonceS, ephS, certHash);
    if (!(await subtle().verify({ name: 'ECDSA', hash: 'SHA-256' }, signer, sig, signed))) throw new Error('bad server signature');

    const pair = this.ephemeral ?? (await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits']));
    const ephC = new Uint8Array(await subtle().exportKey('raw', pair.publicKey));
    const peer = await subtle().importKey('raw', ephS, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
    const z = new Uint8Array(await subtle().deriveBits({ name: 'ECDH', public: peer }, pair.privateKey, 256));
    const salt = new Uint8Array(await subtle().digest('SHA-256', concat(signed, ephC)));
    const [c2s, s2c] = await Promise.all([hkdf(z, salt, 'termenbox-secure-v1 c2s'), hkdf(z, salt, 'termenbox-secure-v1 s2c')]);
    return { message: concat(MAGIC, ephC), send: new Cipher(await aesKey(c2s)), recv: new Cipher(await aesKey(s2c)) };
  }
}

class SecureTransport implements Transport {
  readonly kind = 'ws' as const;
  onmessage: ((text: string) => void) | null = null;
  onclose: (() => void) | null = null;
  readonly lanes = true;
  onterm: ((bytes: Uint8Array) => void) | null = null;
  private closed = false;
  // Records are numbered by arrival and sent in the order they were queued, whatever order the crypto finishes in.
  private inbound: Promise<void> = Promise.resolve();
  private outbound: Promise<void> = Promise.resolve();

  constructor(private ws: WebSocket, private send_: Cipher, private recv: Cipher) {
    ws.onmessage = (e) => {
      // Nothing after the handshake may be unencrypted.
      if (!(e.data instanceof ArrayBuffer)) return this.fail();
      const record = new Uint8Array(e.data);
      const opened = this.recv.open(record);
      this.inbound = this.inbound.then(() => opened).then((plain) => this.deliver(plain), () => this.fail());
    };
    ws.onclose = () => this.finish();
    ws.onerror = () => ws.close();
  }

  private deliver(plain: Bytes) {
    if (this.closed || plain.length === 0) return;
    const body = plain.subarray(1);
    if (plain[0] === LANE_TEXT) {
      let text: string;
      try {
        text = dec.decode(body);
      } catch {
        return this.fail();
      }
      this.onmessage?.(text);
    } else if (plain[0] === LANE_TERM) this.onterm?.(body);
  }

  /** A record that does not open, or a plaintext frame: the connection ends and the owner of it is told. */
  private fail() {
    if (this.closed) return;
    this.ws.close();
    this.finish();
  }

  private put(lane: number, body: Uint8Array): boolean {
    if (this.ws.readyState !== WebSocket.OPEN || 1 + body.length > MAX_PLAIN) return false;
    const sealed = this.send_.seal(concat(new Uint8Array([lane]), body));
    this.outbound = this.outbound.then(() => sealed).then(
      (r) => { if (this.ws.readyState === WebSocket.OPEN) this.ws.send(r); },
      () => this.fail(),
    );
    return true;
  }

  send(text: string) {
    this.put(LANE_TEXT, enc.encode(text));
  }

  sendTerm(bytes: Uint8Array): boolean {
    return this.put(LANE_TERM, bytes);
  }

  sendUpload(frame: Uint8Array): boolean {
    return this.put(LANE_UPLOAD, frame);
  }

  close() {
    this.closed = true;
    this.ws.close();
  }

  private finish() {
    if (this.closed) return;
    this.closed = true;
    this.onclose?.();
  }
}

export interface SecureDeps {
  WebSocket?: typeof WebSocket;
  /** Tests: a handshake with a fixed nonce and key. */
  handshake?: (host: string, pin: Bytes) => Handshake;
}

/**
 * Opens a tunnel path: connects, runs the handshake against the pinned fingerprint, and only then hands out a
 * transport. A server that is not the pinned one never sees a `hello`, and the promise rejects.
 */
export function openSecureWs(url: string, fingerprint: string, timeoutMs: number, signal?: AbortSignal, deps: SecureDeps = {}): Promise<Transport> {
  return new Promise((resolve, reject) => {
    let ws: WebSocket;
    let hs: Handshake;
    let target: string;
    try {
      target = wsUrl(url);
      const host = new URL(target).host.toLowerCase();
      const pin = fingerprintBytes(fingerprint);
      hs = deps.handshake ? deps.handshake(host, pin) : new Handshake(host, pin);
      ws = new (deps.WebSocket ?? WebSocket)(target, [SECURE_SUBPROTOCOL]);
    } catch {
      reject(new Error('invalid tunnel URL or fingerprint'));
      return;
    }
    ws.binaryType = 'arraybuffer';
    let over = false;
    const end = (err?: Error) => {
      if (over) return;
      over = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      if (err) {
        ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
        ws.close();
        reject(err);
      }
    };
    const onAbort = () => end(new Error('cancelled'));
    const timer = setTimeout(() => end(new Error(`no secure WebSocket after ${timeoutMs} ms`)), timeoutMs);
    signal?.addEventListener('abort', onAbort, { once: true });

    let step = 0;
    ws.onopen = () => ws.send(hs.hello());
    ws.onerror = () => end(new Error('WebSocket failed'));
    ws.onclose = () => end(new Error('WebSocket closed before the handshake finished'));
    ws.onmessage = (e) => {
      if (over || step++ > 0 || !(e.data instanceof ArrayBuffer)) return end(new Error('unexpected handshake message'));
      hs.answer(new Uint8Array(e.data)).then(
        ({ message, send, recv }) => {
          if (over) return;
          ws.send(message);
          end();
          resolve(new SecureTransport(ws, send, recv));
        },
        (err) => end(err instanceof Error ? err : new Error('handshake failed')),
      );
    };
  });
}
